import { useCallback, useMemo, useRef, useState } from 'react';
import { useEffect } from 'react';
import type {
  CSSProperties,
  ClipboardEvent as ReactClipboardEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Filter, GripVertical, List as ListIcon, MapPin } from 'lucide-react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { CellRange, GridViewState } from '@stageplottter/core';
import { cellRange, extendRange, rectContains, toggleSort } from '@stageplottter/core';
import { formatShortcut } from '../../../commands/keybinding';
import { currentPlatform } from '../../../lib/platform';
import type { GridAccess } from '../model/grid';
import {
  copyTsv,
  navigate,
  planClear,
  planFill,
  planPaste,
  planSet,
  selectAll,
  selectedRowKeys,
  selectionRect,
  tabTarget,
} from '../model/grid';
import type { FillDir } from '../model/keys';
import { navAction } from '../model/keys';
import {
  dropTarget,
  isNaturalOrder,
  moveColumn,
  orderColumns,
  saveColumnOrder,
  sessionColumnOrder,
} from '../model/columnOrder';
import {
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  RESIZE_STEP,
  effectiveWidths,
  saveColumnWidth,
  sessionColumnWidths,
} from '../model/columnWidths';
import type { GridColumn, GridEdit, GridValue } from '../model/types';
import { isEmptyEdit } from '../model/types';
import { GridIcon } from '../../../ui/StandIcon';
import { useTouchRowDrag } from './useTouchRowDrag';
import {
  applyGridView,
  saveSessionView,
  sessionView,
  setFilter,
  setGroupBy,
  sortDirection,
  toggleOptional,
  visibleOptional,
} from '../model/view';
import {
  clearedValue,
  formatByType,
  formatCell,
  isEditable,
  optionsOf,
  parseCell,
  toggledValue,
} from '../model/values';
import { AutocompleteEditor, MultiSelectEditor, SelectEditor, TextEditor } from './editors';
import type { EditorDone } from './editors';
import type { GridMenuItem } from './GridMenu';
import { GridMenu } from './GridMenu';
import { useCellMarks } from '../highlight';
import './grid.css';

export const COMMENT_ANCHOR_EVENT = 'stageplottter:comment-anchor';

export interface CommentAnchorDetail {
  type: 'row';
  table: string;
  rowKey: string;
  column: string;
}

export interface DataGridProps<R> {
  /** Table id: DOM ids, session view state and comment anchors. */
  id: string;
  label: string;
  rows: readonly R[];
  rowKey(row: R): string;
  columns: readonly GridColumn<R>[];
  /** Extra columns the user can show via the column menu (session only), e.g. custom properties. */
  optionalColumns?: readonly GridColumn<R>[];
  canEdit: boolean;
  onEdit?(edit: GridEdit): void;
  /** Shows a trailing "new row" line; paste may grow the table. */
  allowAppend?: boolean;
  onInsertRow?(rowKey: string, where: 'above' | 'below'): void;
  onDeleteRows?(rowKeys: string[]): void;
  /** Rows linked to the editor selection (highlighted, scrolled to). */
  linkedRowKeys?: ReadonlySet<string>;
  /** Selected rows changed by the user (keyboard / pointer). */
  onRowSelection?(rowKeys: string[]): void;
  /** Moves the active cell to this row and focuses the grid (changes with `nonce`). */
  /** Moves the active cell to a row; `focus: false` only reveals it (keyboard focus stays). */
  focusRequest?: { rowKey: string; nonce: number; focus?: boolean } | null;
  emptyText?: string;
  /** Rows can be commented on (board tables); off where there are no comments (OIL-239). */
  commentable?: boolean;
  toolbar?: ReactNode;
  /** Clipboard bridge (editor UI); falls back to navigator.clipboard. */
  clipboard?: { write(text: string): Promise<void>; read(): Promise<string> };
  /**
   * Rows can go onto the stage (OIL-37): a grip at the start of each row drags the selected rows
   * onto the canvas (HTML5 drag, payload = JSON array of row keys under `mime`); "Place on stage"
   * in the toolbar starts the select → tap mode (iPad) via `onPlace`.
   */
  rowDrag?: {
    mime: string;
    onPlace(rowKeys: string[]): void;
    /** Input list: the selected channels as one list object (OIL-55). */
    onPlaceList?(rowKeys: string[]): void;
  };
  /**
   * Rows can be reordered (OIL-46): Alt+Shift+↑/↓ and the context menu move the selected rows by
   * one; only in the unsorted, ungrouped view (where the order is the stored one).
   */
  onMoveRows?(rowKeys: string[], delta: -1 | 1): void;
}

const NEW_ROW = '__new__';
const ROW_H = 28;
const VIRTUAL_THRESHOLD = 80;
/** Pointer travel (px) before a header press becomes a column drag (a shorter press sorts). */
const DRAG_THRESHOLD = 6;
const DEFAULT_WIDTH: Record<GridColumn<unknown>['type'], number> = {
  text: 150,
  number: 70,
  boolean: 60,
  select: 120,
  multiselect: 200,
  autocomplete: 170,
  send: 64,
};

interface Editing {
  row: number;
  col: number;
  initial: string;
}

interface ColumnDrag {
  pointerId: number;
  from: number;
  startX: number;
  active: boolean;
  /** Target index while dragging and the indicator position (px, relative to the grid). */
  to: number;
  indicator: number;
}

interface MenuState {
  x: number;
  y: number;
  /** Column menu from a header, else cell menu. */
  header: boolean;
}

/** Grid-level key / clipboard handling only applies to data cells (not filters, headers, editors). */
function isDataCell(target: EventTarget | null): boolean {
  return (target as HTMLElement | null)?.classList?.contains('dg-cell') === true;
}

/** True when `target` is a body cell (or an editor inside one) of `grid`. */
function inCell(grid: HTMLElement, target: EventTarget | null): boolean {
  const cell = (target as HTMLElement | null)?.closest?.('.dg-cell');
  return cell !== null && cell !== undefined && grid.contains(cell);
}

const navClipboard = {
  write: (text: string) => navigator.clipboard.writeText(text),
  read: () => navigator.clipboard.readText(),
};

/**
 * The async Clipboard API only exists in secure contexts (HTTPS/localhost). On plain HTTP – e.g. an
 * iPad testing against the LAN stack – keyboard copy/paste must fall through to the native
 * copy/cut/paste events, which work everywhere (handled by onCopy/onPaste).
 */
export function asyncClipboardAvailable(): boolean {
  return typeof navigator !== 'undefined' && navigator.clipboard !== undefined;
}

/**
 * Spreadsheet grid (decision Q14/R13): DOM based, role=grid with roving focus, Excel keyboard
 * semantics, TSV clipboard, fill, context menu, sorting / filtering / grouping (session only).
 * All edits are reported as one {@link GridEdit} per gesture (= one undo step).
 */
export function DataGrid<R>(props: DataGridProps<R>) {
  const {
    id,
    label,
    rows,
    rowKey,
    columns: baseColumns,
    optionalColumns,
    canEdit,
    onEdit,
    allowAppend = false,
    linkedRowKeys,
    onRowSelection,
    focusRequest,
    emptyText,
    commentable = true,
    toolbar,
    rowDrag,
  } = props;
  // Catch-up mode (decision C4): changed cells of this grid, published by another feature.
  const markOf = useCellMarks(id);
  const clipboard = props.clipboard ?? navClipboard;
  const { t } = useTranslation();
  const platform = currentPlatform();
  const mac = platform === 'mac';
  const mod = (k: string) => formatShortcut(`mod+${k}`, platform);

  const [view, setViewState] = useState<GridViewState>(() => sessionView(id));
  const [shownOptional, setShownOptional] = useState<readonly string[]>(() => visibleOptional(id));
  const naturalColumns = useMemo(
    () => [...baseColumns, ...(optionalColumns ?? []).filter((c) => shownOptional.includes(c.key))],
    [baseColumns, optionalColumns, shownOptional],
  );
  // Column order (drag the header / Alt+Shift+←→), session only like sort and filters (Q14).
  const [columnOrder, setColumnOrderState] = useState<readonly string[]>(() =>
    sessionColumnOrder(id),
  );
  const columns = useMemo(
    () => orderColumns(naturalColumns, columnOrder),
    [naturalColumns, columnOrder],
  );
  const [drag, setDrag] = useState<ColumnDrag | null>(null);
  // User column widths (resize handle in the header), session only (OIL-46).
  const [widthOverrides, setWidthOverrides] = useState(() => sessionColumnWidths(id));
  const resize = useRef<{ key: string; startX: number; startW: number; pointerId: number } | null>(
    null,
  );
  const suppressClick = useRef(false);
  const dragAnchor = useRef<{ row: number; col: number } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [range, setRange] = useState<CellRange>(() => cellRange({ row: 0, col: 0 }));
  const [editing, setEditing] = useState<Editing | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [showFilters, setShowFilters] = useState(
    () => Object.keys(sessionView(id).filters).length > 0,
  );
  const [focusWithin, setFocusWithin] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const setColumnOrder = useCallback(
    (next: readonly string[]) => {
      setColumnOrderState(next);
      saveColumnOrder(id, next);
    },
    [id],
  );

  const setView = useCallback(
    (next: GridViewState) => {
      setViewState(next);
      saveSessionView(id, next);
    },
    [id],
  );

  const shown = useMemo(() => applyGridView(rows, columns, view), [rows, columns, view]);
  const dataRows = shown.rows;
  const appendLine = allowAppend && canEdit;
  const navRows = dataRows.length + (appendLine ? 1 : 0);
  const cols = columns.length;

  const colDefs = useMemo<ColumnDef<R>[]>(
    () =>
      columns.map((c) => ({
        id: c.key,
        header: c.header,
        accessorFn: (row: R) => c.value(row),
        meta: c,
      })),
    [columns],
  );
  // eslint-disable-next-line react-hooks/incompatible-library -- table model is recreated from props, never memoised by the compiler
  const table = useReactTable({
    data: dataRows,
    columns: colDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (r) => rowKey(r),
  });
  const headers = table.getHeaderGroups()[0]?.headers ?? [];

  const rowAt = useCallback((r: number): R | undefined => dataRows[r], [dataRows]);
  const access: GridAccess = useMemo(
    () => ({
      rows: dataRows.length,
      cols,
      rowKey: (r) => (r < dataRows.length ? rowKey(dataRows[r]!) : NEW_ROW),
      columnKey: (c) => columns[c]!.key,
      type: (c) => columns[c]!.type,
      value: (r, c) =>
        r < dataRows.length ? columns[c]!.value(dataRows[r]!) : clearedValue(columns[c]!.type),
      text: (r, c) => (r < dataRows.length ? formatCell(columns[c]!, dataRows[r]!) : ''),
      editable: (r, c) => canEdit && isEditable(columns[c]!, dataRows[r]),
      parse: (r, c, text) => parseCell(columns[c]!, text, dataRows[r]),
    }),
    [dataRows, cols, columns, rowKey, canEdit],
  );

  // Keep the selection inside the grid when rows disappear.
  const clampCell = (cell: { row: number; col: number }) => ({
    row: Math.min(Math.max(cell.row, 0), Math.max(navRows - 1, 0)),
    col: Math.min(Math.max(cell.col, 0), Math.max(cols - 1, 0)),
  });
  const focus = clampCell(range.focus);
  const rect = selectionRect({ anchor: clampCell(range.anchor), focus }, navRows, cols);

  // ------------------------------------------------------------ external selection / focus requests
  const [prevLinked, setPrevLinked] = useState(linkedRowKeys);
  if (prevLinked !== linkedRowKeys) {
    setPrevLinked(linkedRowKeys);
    const activeKey = focus.row < dataRows.length ? rowKey(dataRows[focus.row]!) : null;
    if (
      linkedRowKeys &&
      linkedRowKeys.size > 0 &&
      !focusWithin &&
      (activeKey === null || !linkedRowKeys.has(activeKey))
    ) {
      const idx = dataRows.findIndex((r) => linkedRowKeys.has(rowKey(r)));
      if (idx >= 0) setRange(cellRange({ row: idx, col: focus.col }));
    }
  }
  const [prevRequest, setPrevRequest] = useState(focusRequest);
  const [pendingFocus, setPendingFocus] = useState(0);
  const [pendingReveal, setPendingReveal] = useState<{ row: number; n: number } | null>(null);
  if (prevRequest !== focusRequest) {
    setPrevRequest(focusRequest);
    if (focusRequest) {
      const idx = dataRows.findIndex((r) => rowKey(r) === focusRequest.rowKey);
      if (idx >= 0) {
        setRange(cellRange({ row: idx, col: 0 }));
        if (focusRequest.focus === false) setPendingReveal({ row: idx, n: Date.now() });
        else setPendingFocus((n) => n + 1);
      }
    }
  }

  // ------------------------------------------------------------ virtualisation (large tables only)
  const lineCount = shown.lines.length + (appendLine ? 1 : 0);
  const virtual = lineCount > VIRTUAL_THRESHOLD;
  const virtualizer = useVirtualizer({
    count: virtual ? lineCount : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });
  const lineOfRow = useMemo(() => {
    const m = new Map<number, number>();
    shown.lines.forEach((l, i) => {
      if (l.type === 'row') m.set(l.index, i);
    });
    m.set(dataRows.length, shown.lines.length);
    return m;
  }, [shown.lines, dataRows.length]);

  const cellId = (r: number, c: number) => `dg-${id}-${r}-${c}`;

  // a row marked from elsewhere without taking the focus (quick entry): scroll it into view
  useEffect(() => {
    if (!pendingReveal) return;
    const el = document.getElementById(cellId(pendingReveal.row, 0));
    if (el) el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    else if (virtual) virtualizer.scrollToIndex(lineOfRow.get(pendingReveal.row) ?? 0);
    // cellId is derived from id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReveal, virtual, virtualizer, lineOfRow]);

  const consumedFocus = useRef(0);
  useEffect(() => {
    const forced = pendingFocus !== consumedFocus.current;
    if ((!focusWithin && !forced) || editing || menu) return;
    consumedFocus.current = pendingFocus;
    const el = document.getElementById(cellId(focus.row, focus.col));
    if (el) {
      if (document.activeElement !== el) el.focus({ preventScroll: false });
      el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    } else if (virtual) virtualizer.scrollToIndex(lineOfRow.get(focus.row) ?? 0);
    // cellId is derived from id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    focus.row,
    focus.col,
    focusWithin,
    editing,
    menu,
    pendingFocus,
    virtual,
    virtualizer,
    lineOfRow,
  ]);

  // ------------------------------------------------------------ selection → shared selection
  const selectedKeys = useMemo(() => selectedRowKeys(access, rect), [access, rect]);
  const selectedSig = selectedKeys.join('\n');
  // Re-runs when the rows' data changes too (onRowSelection is re-created), so a row whose source
  // item was just created selects it.
  useEffect(() => {
    if (!focusWithin) return;
    onRowSelection?.(selectedSig === '' ? [] : selectedSig.split('\n'));
  }, [selectedSig, focusWithin, onRowSelection]);

  // ------------------------------------------------------------ edits
  const apply = (edit: GridEdit) => {
    if (!isEmptyEdit(edit)) onEdit?.(edit);
  };

  // Not clamped here: after appending a row the grid is one line longer on the next render.
  const moveTo = (row: number, col: number) =>
    setRange(
      cellRange({ row: Math.max(row, 0), col: Math.min(Math.max(col, 0), Math.max(cols - 1, 0)) }),
    );

  const startEdit = (r: number, c: number, initial?: string) => {
    const col = columns[c]!;
    if (!access.editable(r, c)) return;
    if (col.type === 'boolean' || col.type === 'send') {
      if (initial === undefined || initial === ' ') {
        const v = toggledValue(col.type, access.value(r, c));
        if (v !== undefined) apply(planSet(access, r, c, v));
        return;
      }
      if (col.type === 'boolean') {
        const v = parseCell(col, initial, rowAt(r));
        if (typeof v === 'boolean') apply(planSet(access, r, c, v));
        return;
      }
    }
    let text = initial;
    if (text === undefined) {
      const v = access.value(r, c);
      text = col.type === 'select' || col.type === 'multiselect' ? String(v) : access.text(r, c);
      if (col.type === 'send') text = typeof v === 'number' ? String(v) : '';
    } else if (col.type === 'select') {
      const opts = optionsOf(col, rowAt(r));
      text =
        opts.find((o) => o.label.toLowerCase().startsWith(text!.toLowerCase()))?.value ??
        String(access.value(r, c));
    }
    setEditing({ row: r, col: c, initial: text });
  };

  const done: EditorDone = {
    commit: (result, dRow, dCol) => {
      const e = editing;
      if (!e) return;
      setEditing(null);
      const col = columns[e.col]!;
      const value: GridValue | undefined =
        'text' in result ? parseCell(col, result.text, rowAt(e.row)) : result.value;
      if (value !== undefined) apply(planSet(access, e.row, e.col, value));
      if (dRow !== 0 || dCol !== 0) {
        const target =
          dCol !== 0
            ? tabTarget(
                { row: e.row, col: e.col },
                dCol < 0,
                navRows + (e.row === dataRows.length ? 1 : 0),
                cols,
              )
            : null;
        if (target) moveTo(target.row, target.col);
        else moveTo(e.row + dRow, e.col);
      }
    },
    cancel: () => setEditing(null),
  };

  const fillSel = (dir: FillDir) => rect && apply(planFill(access, rect, dir));

  const copySel = async (cut: boolean) => {
    if (!rect) return;
    const r = { ...rect, bottom: Math.min(rect.bottom, dataRows.length - 1) };
    if (r.bottom < r.top) return;
    await clipboard.write(copyTsv(access, r)).catch(() => undefined);
    if (cut) apply(planClear(access, r));
  };

  const pasteText = (text: string) => {
    if (!rect || !canEdit) return;
    const plan = planPaste(access, rect, text, allowAppend);
    apply(plan.edit);
    setRange(plan.range);
  };

  const pasteFromClipboard = async () => {
    const text = await clipboard.read().catch(() => '');
    if (text !== '') pasteText(text);
  };

  const commentOnRow = () => {
    if (focus.row >= dataRows.length) return;
    const detail: CommentAnchorDetail = {
      type: 'row',
      table: id,
      rowKey: rowKey(dataRows[focus.row]!),
      column: columns[focus.col]!.key,
    };
    (gridRef.current ?? window).dispatchEvent(
      new CustomEvent(COMMENT_ANCHOR_EVENT, { bubbles: true, detail }),
    );
  };

  const openMenu = (x: number, y: number, header: boolean) =>
    setMenu({
      x: Math.min(x, window.innerWidth - 250),
      y: Math.min(y, window.innerHeight - 380),
      header,
    });

  const closeMenu = useCallback(() => {
    setMenu(null);
    setPendingFocus((n) => n + 1);
  }, []);

  const openMenuAtCell = () => {
    const el = document.getElementById(cellId(focus.row, focus.col));
    const r = el?.getBoundingClientRect();
    openMenu(r ? r.left + 8 : 40, r ? r.bottom : 40, false);
  };

  // ------------------------------------------------------------ keyboard
  const focusHeader = (c: number) => document.getElementById(`dg-${id}-h-${c}`)?.focus();

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (editing || menu || !isDataCell(e.target)) return;
    const modKey = mac ? e.metaKey : e.ctrlKey;
    const k = e.key.toLowerCase();
    if (modKey && !e.altKey && !e.shiftKey && (k === 'c' || k === 'x' || k === 'v')) {
      // Without the async Clipboard API let the browser fire its native clipboard events.
      if (!asyncClipboardAvailable()) return;
      e.preventDefault();
      e.stopPropagation();
      if (k === 'v') void pasteFromClipboard();
      else void copySel(k === 'x' && canEdit);
      return;
    }
    if (
      e.altKey &&
      e.shiftKey &&
      (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
      props.onMoveRows
    ) {
      e.preventDefault();
      e.stopPropagation();
      moveRows(e.key === 'ArrowUp' ? -1 : 1);
      return;
    }
    const a = navAction(e, mac);
    let handled = true;
    switch (a.type) {
      case 'move':
        if (a.dRow < 0 && focus.row === 0 && !a.extend) focusHeader(focus.col);
        else setRange(navigate({ anchor: clampCell(range.anchor), focus }, a, navRows, cols)!);
        break;
      case 'edge':
      case 'home':
      case 'end':
      case 'page':
        setRange(navigate({ anchor: clampCell(range.anchor), focus }, a, navRows, cols, 10)!);
        break;
      case 'tab': {
        const next = tabTarget(focus, a.back, navRows, cols);
        if (next) moveTo(next.row, next.col);
        else handled = false;
        break;
      }
      case 'enter':
        moveTo(focus.row - 1, focus.col);
        break;
      case 'edit':
        startEdit(focus.row, focus.col);
        break;
      case 'type':
        startEdit(focus.row, focus.col, a.text);
        break;
      case 'space': {
        const col = columns[focus.col]!;
        if ((col.type === 'boolean' || col.type === 'send') && rect) {
          const v = toggledValue(col.type, access.value(focus.row, focus.col));
          const edit: GridEdit = { changes: [], appended: [] };
          for (let r = rect.top; r <= rect.bottom; r++) {
            const one = planSet(access, r, focus.col, v!);
            edit.changes.push(...one.changes);
            edit.appended.push(...one.appended);
          }
          apply(edit);
        } else startEdit(focus.row, focus.col, col.type === 'multiselect' ? undefined : ' ');
        break;
      }
      case 'clear':
        if (rect) apply(planClear(access, rect));
        break;
      case 'selectAll':
        setRange(selectAll(navRows, cols));
        break;
      case 'fill':
        fillSel(a.dir);
        break;
      case 'menu':
        openMenuAtCell();
        break;
      case 'escape':
        if (rect && (rect.top !== rect.bottom || rect.left !== rect.right))
          setRange(cellRange(focus));
        else handled = false;
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onCopy = (e: ReactClipboardEvent) => {
    if (editing || !rect || !isDataCell(e.target)) return;
    e.preventDefault();
    e.clipboardData.setData(
      'text/plain',
      copyTsv(access, { ...rect, bottom: Math.min(rect.bottom, dataRows.length - 1) }),
    );
  };
  const onCut = (e: ReactClipboardEvent) => {
    if (editing || !rect || !canEdit || !isDataCell(e.target)) return;
    const r = { ...rect, bottom: Math.min(rect.bottom, dataRows.length - 1) };
    if (r.bottom < r.top) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', copyTsv(access, r));
    apply(planClear(access, r));
  };
  const onPaste = (e: ReactClipboardEvent) => {
    if (editing || !isDataCell(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    pasteText(e.clipboardData.getData('text/plain'));
  };

  const onCellPointer = (e: ReactMouseEvent, r: number, c: number) => {
    if (e.button === 2 && rect && rectContains(rect, { row: r, col: c })) return;
    if (e.button === 0) {
      // drag selects cells, never text (2026-10-06: a click + drag started a text selection)
      const inEditor = (e.target as Element).closest?.('input, textarea, select');
      if (!inEditor) {
        e.preventDefault();
        (e.currentTarget as HTMLElement).focus({ preventScroll: true });
        dragAnchor.current = e.shiftKey ? clampCell(range.anchor) : { row: r, col: c };
        const end = () => {
          dragAnchor.current = null;
          window.removeEventListener('mouseup', end);
        };
        window.addEventListener('mouseup', end);
      }
    }
    if (e.shiftKey)
      setRange(extendRange({ anchor: clampCell(range.anchor), focus }, { row: r, col: c }));
    else setRange(cellRange({ row: r, col: c }));
  };
  /** Mouse moved onto a cell with the button held: the range follows. */
  const onCellDrag = (e: ReactMouseEvent, r: number, c: number) => {
    const anchor = dragAnchor.current;
    if (anchor === null || (e.buttons & 1) === 0) return;
    setRange(extendRange({ anchor, focus: anchor }, { row: r, col: c }));
  };

  // ------------------------------------------------------------ header
  /** Moves column `from` to `to`; keeps the active cell on it and announces the new position. */
  const reorderColumn = (from: number, to: number, refocus: boolean) => {
    const keys = columns.map((col) => col.key);
    const next = moveColumn(keys, from, to);
    const at = next.indexOf(keys[from]!);
    if (at === from) return;
    setColumnOrder(next);
    if (focus.col === from) setRange(cellRange({ row: focus.row, col: at }));
    setAnnouncement(t('grid.columnMoved', { column: columns[from]!.header, position: at + 1 }));
    if (refocus) setTimeout(() => focusHeader(at), 0);
  };

  const onHeaderKey = (e: ReactKeyboardEvent, c: number) => {
    if (e.altKey && e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      reorderColumn(c, c + (e.key === 'ArrowRight' ? 1 : -1), true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showFilters) document.getElementById(`dg-${id}-f-${c}`)?.focus();
      else {
        moveTo(0, c);
        document.getElementById(cellId(0, c))?.focus();
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      focusHeader(Math.min(Math.max(c + (e.key === 'ArrowRight' ? 1 : -1), 0), cols - 1));
    } else if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      e.preventDefault();
      setRange(cellRange({ row: focus.row, col: c }));
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      openMenu(r.left, r.bottom, true);
    } else return;
    e.stopPropagation();
  };

  // ------------------------------------------------------------ header drag (pointer, touch, pen)
  const headerRects = () => {
    const grid = gridRef.current;
    const origin = grid?.getBoundingClientRect().left ?? 0;
    const els = grid?.querySelectorAll<HTMLElement>('.dg-header-row > [role="columnheader"]');
    return [...(els ?? [])].map((el) => {
      const r = el.getBoundingClientRect();
      return { left: r.left - origin, width: r.width };
    });
  };

  const onHeaderPointerDown = (e: ReactPointerEvent<HTMLButtonElement>, c: number) => {
    if (e.button !== 0 || cols < 2) return;
    setDrag({
      pointerId: e.pointerId,
      from: c,
      startX: e.clientX,
      active: false,
      to: c,
      indicator: 0,
    });
  };

  const onHeaderPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (!drag.active && Math.abs(e.clientX - drag.startX) < DRAG_THRESHOLD) return;
    if (!drag.active) e.currentTarget.setPointerCapture?.(e.pointerId);
    const rects = headerRects();
    const x = e.clientX - (gridRef.current?.getBoundingClientRect().left ?? 0);
    const to = dropTarget(drag.from, x, rects);
    // Indicator at the slot the column is inserted into.
    const slot = to > drag.from ? to + 1 : to;
    const edge =
      slot >= rects.length
        ? (rects.at(-1)?.left ?? 0) + (rects.at(-1)?.width ?? 0)
        : (rects[slot]?.left ?? 0);
    setDrag({ ...drag, active: true, to, indicator: edge });
  };

  const onHeaderPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    setDrag(null);
    if (!drag.active) return;
    // The click that follows a drag must not sort.
    suppressClick.current = true;
    setTimeout(() => (suppressClick.current = false), 0);
    reorderColumn(drag.from, drag.to, false);
  };

  const toBody = (c: number) => {
    moveTo(Math.min(focus.row, Math.max(navRows - 1, 0)), c);
    document.getElementById(cellId(Math.min(focus.row, Math.max(navRows - 1, 0)), c))?.focus();
  };

  // ------------------------------------------------------------ row order
  const rowsMovable = view.sort.length === 0 && view.groupBy === null;
  /** Moves the selected rows (else the active one) by one; the active cell moves along. */
  function moveRows(delta: -1 | 1) {
    if (!props.onMoveRows || !canEdit) return;
    if (!rowsMovable) {
      setAnnouncement(t('grid.moveRowsSorted'));
      return;
    }
    const keys = selectedKeys.filter((k) => k !== NEW_ROW);
    const at = focus.row + delta;
    if (keys.length === 0 || at < 0 || at >= dataRows.length) return;
    props.onMoveRows(keys, delta);
    setRange(cellRange({ row: at, col: focus.col }));
    setAnnouncement(t('grid.rowsMoved', { count: keys.length, position: at + 1 }));
  }

  // ------------------------------------------------------------ touch drag of rows (U74)
  const touchDrag = useTouchRowDrag({
    gridId: id,
    mime: rowDrag?.mime,
    keysFor: (key) => {
      const keys = selectedKeys.filter((k) => k !== NEW_ROW);
      return keys.includes(key) ? keys : [key];
    },
    order: props.onMoveRows && rowsMovable ? dataRows.map(rowKey) : undefined,
    ...(props.onMoveRows ? { move: props.onMoveRows } : {}),
    label: (count) => t('grid.dragGhost', { count }),
  });

  // ------------------------------------------------------------ context menu
  const hasRow = focus.row < dataRows.length;
  const activeKey = hasRow ? rowKey(dataRows[focus.row]!) : null;
  const focusCol = columns[focus.col];
  const menuItems: GridMenuItem[] = [
    { id: 'copy', label: t('grid.copy'), hint: mod('c'), run: () => void copySel(false) },
    {
      id: 'paste',
      label: t('grid.paste'),
      hint: mod('v'),
      disabled: !canEdit,
      run: () => void pasteFromClipboard(),
    },
    {
      id: 'fill-down',
      label: t('grid.fillDown'),
      hint: mod('d'),
      disabled: !canEdit,
      separatorBefore: true,
      run: () => fillSel('down'),
    },
    { id: 'fill-up', label: t('grid.fillUp'), disabled: !canEdit, run: () => fillSel('up') },
    {
      id: 'fill-right',
      label: t('grid.fillRight'),
      hint: mod('r'),
      disabled: !canEdit,
      run: () => fillSel('right'),
    },
    { id: 'fill-left', label: t('grid.fillLeft'), disabled: !canEdit, run: () => fillSel('left') },
  ];
  if (props.onInsertRow) {
    const insert = props.onInsertRow;
    menuItems.push(
      {
        id: 'insert-above',
        label: t('grid.insertAbove'),
        disabled: !canEdit || activeKey === null,
        separatorBefore: true,
        run: () => activeKey && insert(activeKey, 'above'),
      },
      {
        id: 'insert-below',
        label: t('grid.insertBelow'),
        disabled: !canEdit || activeKey === null,
        run: () => activeKey && insert(activeKey, 'below'),
      },
    );
  }
  if (props.onMoveRows) {
    const disabled = !canEdit || activeKey === null || !rowsMovable;
    menuItems.push(
      {
        id: 'move-up',
        label: t('grid.moveRowsUp'),
        hint: 'Alt+Shift+↑',
        disabled: disabled || focus.row === 0,
        separatorBefore: !props.onInsertRow,
        run: () => moveRows(-1),
      },
      {
        id: 'move-down',
        label: t('grid.moveRowsDown'),
        hint: 'Alt+Shift+↓',
        disabled: disabled || focus.row >= dataRows.length - 1,
        run: () => moveRows(1),
      },
    );
  }
  if (props.onDeleteRows) {
    const del = props.onDeleteRows;
    menuItems.push({
      id: 'delete-rows',
      label: t('grid.deleteRows', {
        count: Math.max(selectedKeys.filter((k) => k !== NEW_ROW).length, 1),
      }),
      disabled: !canEdit || selectedKeys.filter((k) => k !== NEW_ROW).length === 0,
      run: () => del(selectedKeys.filter((k) => k !== NEW_ROW)),
    });
  }
  if (commentable)
    menuItems.push({
      id: 'comment',
      label: t('grid.comment'),
      disabled: !hasRow,
      separatorBefore: true,
      run: commentOnRow,
    });
  if (focusCol) {
    const dir = sortDirection(view, focusCol.key);
    menuItems.push(
      {
        id: 'sort-asc',
        label: t('grid.sortAsc', { column: focusCol.header }),
        separatorBefore: true,
        disabled: dir === 'ascending',
        run: () => setView({ ...view, sort: [{ key: focusCol.key, dir: 'asc' }] }),
      },
      {
        id: 'sort-desc',
        label: t('grid.sortDesc', { column: focusCol.header }),
        disabled: dir === 'descending',
        run: () => setView({ ...view, sort: [{ key: focusCol.key, dir: 'desc' }] }),
      },
      view.groupBy === focusCol.key
        ? { id: 'ungroup', label: t('grid.ungroup'), run: () => setView(setGroupBy(view, null)) }
        : {
            id: 'group',
            label: t('grid.groupBy', { column: focusCol.header }),
            run: () => setView(setGroupBy(view, focusCol.key)),
          },
      {
        id: 'filter',
        label: t('grid.filterColumn', { column: focusCol.header }),
        run: () => {
          setShowFilters(true);
          setTimeout(() => document.getElementById(`dg-${id}-f-${focus.col}`)?.focus(), 0);
        },
      },
    );
  }

  menuItems.push({
    id: 'reset-column-order',
    label: t('grid.resetColumnOrder'),
    separatorBefore: true,
    disabled: isNaturalOrder(
      columnOrder,
      naturalColumns.map((c) => c.key),
    ),
    run: () => {
      setColumnOrder([]);
      setAnnouncement(t('grid.columnOrderReset'));
    },
  });

  (optionalColumns ?? []).forEach((c, i) => {
    const on = shownOptional.includes(c.key);
    menuItems.push({
      id: `col-${c.key}`,
      label: on
        ? t('grid.hideColumn', { column: c.header })
        : t('grid.showColumn', { column: c.header }),
      separatorBefore: i === 0,
      run: () => setShownOptional(toggleOptional(id, c.key)),
    });
  });

  // ------------------------------------------------------------ render
  const widths = effectiveWidths(columns, widthOverrides, (c) => DEFAULT_WIDTH[c.type]);
  const setWidth = (key: string, w: number | null) =>
    setWidthOverrides(saveColumnWidth(id, key, w));
  const totalWidth = widths.reduce((a, b) => a + b, 0);
  const headerRows = showFilters ? 2 : 1;
  const linked = linkedRowKeys ?? new Set<string>();
  const filterCount = Object.keys(view.filters).length;

  const renderCell = (r: number, c: number) => {
    const col = columns[c]!;
    const row = rowAt(r);
    const selected = rect !== undefined && rectContains(rect, { row: r, col: c });
    const active = focus.row === r && focus.col === c;
    const editable = access.editable(r, c);
    const isEditing = editing !== null && editing.row === r && editing.col === c;
    const value = row === undefined ? clearedValue(col.type) : col.value(row);
    const text =
      row === undefined
        ? ''
        : col.format
          ? col.format(value, row)
          : formatByType(col.type, value, optionsOf(col, row));
    const mark = row === undefined ? null : markOf(rowKey(row), col.key);
    const classes = [
      'dg-cell',
      `dg-${col.type}`,
      selected ? 'is-selected' : '',
      active ? 'is-active' : '',
      editable ? '' : 'is-readonly',
      row && col.cellClass?.(row),
      col.align === 'end' || col.type === 'number' ? 'is-end' : '',
      mark ? `is-cu-${mark}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    const editorLabel = `${col.header}, ${t('grid.row', { n: r + 1 })}`;
    const icon = row === undefined ? undefined : col.icon?.(row);
    let content: ReactNode =
      icon === undefined ? (
        text
      ) : (
        <span className="dg-with-icon">
          <GridIcon icon={icon} />
          {text}
        </span>
      );
    const grip =
      (rowDrag || props.onMoveRows) && row !== undefined && c === 0 && canEdit ? (
        <span
          className="dg-grip"
          draggable={rowDrag !== undefined}
          aria-hidden="true"
          title={t('grid.dragRows')}
          data-testid="dg-grip"
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            // a touch long press drags with pointer events instead (U74)
            if (!rowDrag || touchDrag.active()) {
              e.preventDefault();
              return;
            }
            const key = rowKey(row);
            const keys = selectedKeys.includes(key) ? selectedKeys : [key];
            e.dataTransfer.setData(rowDrag.mime, JSON.stringify(keys));
            e.dataTransfer.effectAllowed = 'copyMove';
          }}
        >
          <GripVertical size={12} />
        </span>
      ) : null;
    if (isEditing) {
      const common = { ...done, label: editorLabel };
      if (col.type === 'select')
        content = (
          <SelectEditor {...common} initial={editing.initial} options={optionsOf(col, row)} />
        );
      else if (col.type === 'multiselect')
        content = (
          <MultiSelectEditor
            {...common}
            initial={Array.isArray(value) ? value : []}
            options={optionsOf(col, row)}
          />
        );
      else if (col.type === 'autocomplete' && col.suggest) {
        const suggest = col.suggest;
        content = (
          <AutocompleteEditor
            {...common}
            initial={editing.initial}
            suggest={(q) => suggest(q, row)}
          />
        );
      } else
        content = (
          <TextEditor
            {...common}
            initial={editing.initial}
            numeric={col.type === 'number' || col.type === 'send'}
          />
        );
    } else if (col.type === 'boolean') {
      content = (
        <>
          <span aria-hidden="true" className="dg-bool">
            {value === true ? '✓' : ''}
          </span>
          {/* the empty "+" line has no value yet: no "No" for a screen reader (OIL-267) */}
          {row !== undefined && (
            <span className="sr-only">{value ? t('grid.yes') : t('grid.no')}</span>
          )}
        </>
      );
    }
    return (
      <div
        key={c}
        id={cellId(r, c)}
        role="gridcell"
        aria-colindex={c + 1}
        aria-selected={selected}
        aria-readonly={editable ? undefined : true}
        tabIndex={active ? 0 : -1}
        className={classes}
        style={{ width: widths[c] }}
        title={row ? col.title?.(row) : undefined}
        onMouseDown={(e) => onCellPointer(e, r, c)}
        onMouseEnter={(e) => onCellDrag(e, r, c)}
        onDoubleClick={() => startEdit(r, c)}
        data-has-grip={grip ? true : undefined}
        // touch: a long press anywhere on the row header (grip + number) drags the rows (U74)
        {...(grip && row !== undefined ? touchDrag.gripProps(rowKey(row)) : {})}
        onContextMenu={(e) => {
          e.preventDefault();
          // a touch long press on the row header drags instead (U74)
          if (grip && touchDrag.active()) return;
          if (!(rect && rectContains(rect, { row: r, col: c })))
            setRange(cellRange({ row: r, col: c }));
          openMenu(e.clientX, e.clientY, false);
        }}
      >
        {grip}
        {content}
      </div>
    );
  };

  const renderLine = (i: number, style?: CSSProperties) => {
    if (i === shown.lines.length) {
      const r = dataRows.length;
      return (
        <div
          key={NEW_ROW}
          role="row"
          aria-rowindex={headerRows + i + 1}
          className="dg-row dg-new-row"
          style={style}
          title={t('grid.newRowHint')}
        >
          {columns.map((_, c) => renderCell(r, c))}
        </div>
      );
    }
    const line = shown.lines[i]!;
    if (line.type === 'group') {
      return (
        <div
          key={`g:${line.key}:${i}`}
          role="row"
          aria-rowindex={headerRows + i + 1}
          className="dg-row dg-group-row"
          style={style}
        >
          <div
            role="gridcell"
            aria-colindex={1}
            aria-colspan={cols}
            className="dg-group-cell"
            style={{ width: totalWidth }}
          >
            {line.key === '' ? t('grid.emptyGroup') : line.key}{' '}
            <span className="muted">({line.count})</span>
          </div>
        </div>
      );
    }
    const key = rowKey(line.row);
    const tableRow = table.getRow(key);
    return (
      <div
        key={key}
        role="row"
        aria-rowindex={headerRows + i + 1}
        aria-selected={linked.has(key) || undefined}
        className={`dg-row${linked.has(key) ? ' is-linked' : ''}`}
        style={style}
        data-row-key={tableRow.id}
      >
        {tableRow.getVisibleCells().map((_, c) => renderCell(line.index, c))}
      </div>
    );
  };

  const body = virtual ? (
    <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: totalWidth }}>
      {virtualizer.getVirtualItems().map((v) =>
        renderLine(v.index, {
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `translateY(${v.start}px)`,
          height: ROW_H,
        }),
      )}
    </div>
  ) : (
    Array.from({ length: lineCount }, (_, i) => renderLine(i))
  );

  return (
    <div className="dg" data-grid-id={id}>
      {touchDrag.ghost}
      <div className="dg-toolbar" role="toolbar" aria-label={t('grid.toolbar', { name: label })}>
        {toolbar}
        {rowDrag && (
          <button
            type="button"
            className="btn btn-small"
            disabled={!canEdit || selectedKeys.length === 0}
            title={t('grid.placeOnStageHint')}
            onClick={() => rowDrag.onPlace(selectedKeys)}
          >
            <MapPin size={13} aria-hidden="true" /> {t('grid.placeOnStage')}
          </button>
        )}
        {rowDrag?.onPlaceList && (
          <button
            type="button"
            className="btn btn-small"
            disabled={!canEdit || selectedKeys.filter((k) => k !== NEW_ROW).length === 0}
            title={t('grid.placeAsListHint')}
            onClick={() => rowDrag.onPlaceList!(selectedKeys.filter((k) => k !== NEW_ROW))}
          >
            <ListIcon size={13} aria-hidden="true" /> {t('grid.placeAsList')}
          </button>
        )}
        <span className="spacer" />
        <label className="dg-group-select">
          <span className="muted small">{t('grid.groupLabel')}</span>
          <select
            value={view.groupBy ?? ''}
            onChange={(e) =>
              setView(setGroupBy(view, e.target.value === '' ? null : e.target.value))
            }
          >
            <option value="">{t('grid.noGrouping')}</option>
            {columns.map((c) => (
              <option key={c.key} value={c.key}>
                {c.header}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={`btn btn-small${filterCount > 0 ? ' is-on' : ''}`}
          aria-pressed={showFilters}
          onClick={() => setShowFilters((s) => !s)}
        >
          <Filter size={13} aria-hidden="true" />{' '}
          {filterCount > 0 ? t('grid.filtersActive', { count: filterCount }) : t('grid.filter')}
        </button>
      </div>
      <div className="dg-scroll" ref={scrollRef}>
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus -- roving tabindex: the active cell is focusable */}
        <div
          ref={gridRef}
          id={`dg-${id}`}
          role="grid"
          aria-label={label}
          aria-rowcount={headerRows + lineCount}
          aria-colcount={cols}
          aria-multiselectable="true"
          aria-readonly={canEdit ? undefined : true}
          className="dg-grid"
          style={{ width: totalWidth }}
          onKeyDown={onKeyDown}
          onCopy={onCopy}
          onCut={onCut}
          onPaste={onPaste}
          onFocus={(e) => setFocusWithin(inCell(e.currentTarget, e.target))}
          onBlur={(e) => {
            if (!inCell(e.currentTarget, e.relatedTarget)) setFocusWithin(false);
          }}
        >
          <div role="rowgroup" className="dg-head">
            <div role="row" aria-rowindex={1} className="dg-row dg-header-row">
              {headers.map((h, c) => {
                const col = h.column.columnDef.meta as GridColumn<R>;
                const dir = sortDirection(view, col.key);
                return (
                  <div
                    key={h.id}
                    role="columnheader"
                    aria-label={col.header}
                    aria-colindex={c + 1}
                    aria-sort={dir}
                    className={`dg-header${drag?.active && drag.from === c ? ' is-dragging' : ''}`}
                    style={{ width: widths[c] }}
                  >
                    <button
                      type="button"
                      id={`dg-${id}-h-${c}`}
                      tabIndex={-1}
                      className="dg-sort"
                      aria-describedby={`dg-${id}-reorder-hint`}
                      onClick={(e) => {
                        if (suppressClick.current) return;
                        setView(toggleSort(view, col.key, e.shiftKey));
                      }}
                      onKeyDown={(e) => onHeaderKey(e, c)}
                      onPointerDown={(e) => onHeaderPointerDown(e, c)}
                      onPointerMove={onHeaderPointerMove}
                      onPointerUp={onHeaderPointerUp}
                      onPointerCancel={() => setDrag(null)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setRange(cellRange({ row: focus.row, col: c }));
                        openMenu(e.clientX, e.clientY, true);
                      }}
                    >
                      <span>{col.header}</span>
                      {dir === 'ascending' && <ArrowUp size={12} aria-hidden="true" />}
                      {dir === 'descending' && <ArrowDown size={12} aria-hidden="true" />}
                      {view.groupBy === col.key && (
                        <span className="dg-grouped">{t('grid.grouped')}</span>
                      )}
                    </button>
                    {/* A focusable separator is an interactive widget (ARIA "window splitter"). */}
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={t('grid.resizeColumn', { column: col.header })}
                      aria-valuenow={widths[c]}
                      aria-valuemin={MIN_COLUMN_WIDTH}
                      aria-valuemax={MAX_COLUMN_WIDTH}
                      tabIndex={-1}
                      className="dg-resize"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.setPointerCapture?.(e.pointerId);
                        resize.current = {
                          key: col.key,
                          startX: e.clientX,
                          startW: widths[c]!,
                          pointerId: e.pointerId,
                        };
                      }}
                      onPointerMove={(e) => {
                        const r = resize.current;
                        if (!r || r.pointerId !== e.pointerId) return;
                        setWidth(r.key, r.startW + e.clientX - r.startX);
                      }}
                      onPointerUp={() => (resize.current = null)}
                      onPointerCancel={() => (resize.current = null)}
                      onDoubleClick={() => setWidth(col.key, null)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                          e.preventDefault();
                          e.stopPropagation();
                          setWidth(
                            col.key,
                            widths[c]! + (e.key === 'ArrowRight' ? 1 : -1) * RESIZE_STEP,
                          );
                        }
                      }}
                    />
                  </div>
                );
              })}
              {drag?.active && (
                <div
                  className="dg-drop-indicator"
                  style={{ left: drag.indicator }}
                  aria-hidden="true"
                />
              )}
            </div>
            {showFilters && (
              <div role="row" aria-rowindex={2} className="dg-row dg-filter-row">
                {columns.map((col, c) => (
                  <div
                    key={col.key}
                    role="gridcell"
                    aria-colindex={c + 1}
                    className="dg-filter"
                    style={{ width: widths[c] }}
                  >
                    <input
                      id={`dg-${id}-f-${c}`}
                      type="search"
                      aria-label={t('grid.filterFor', { column: col.header })}
                      placeholder={t('grid.contains')}
                      value={view.filters[col.key] ?? ''}
                      onChange={(e) => setView(setFilter(view, col.key, e.target.value))}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          if (e.key === 'Escape' && e.currentTarget.value !== '')
                            setView(setFilter(view, col.key, ''));
                          else toBody(c);
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          focusHeader(c);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div role="rowgroup" className="dg-body">
            {body}
          </div>
        </div>
        {/* also under the "+" line: a filter may hide every row (OIL-231) */}
        {shown.lines.length === 0 && (
          <p className="muted small dg-empty">
            {rows.length > 0 ? t('grid.noMatch') : (emptyText ?? t('grid.empty'))}
          </p>
        )}
      </div>
      <span id={`dg-${id}-reorder-hint`} className="sr-only">
        {t('grid.reorderHint')}
      </span>
      <div className="sr-only" aria-live="polite" role="status">
        {announcement}
      </div>
      {menu && (
        <GridMenu
          x={menu.x}
          y={menu.y}
          label={t('grid.menu', { name: label })}
          items={menuItems}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}
